import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DISCIPLINES, type DisciplineSlug } from "@jumpbean/shared";
import { Card, CharacterImage, GLOW_COLORS } from "../components";
import { usePageTitle } from "../hooks";
import { useAppStore } from "../stores";
import { apiGet } from "../lib/api";

// Magical world names for each discipline
const WORLD_NAMES: Record<DisciplineSlug, string> = {
  math: "Starlight Castle",
  spelling: "Fairy Garden",
  patterns: "Rainbow Bridge",
  typing: "Keyboard Kingdom",
  programming: "Code Quest",
};

// Character taglines
const CHARACTER_TAGLINES: Record<DisciplineSlug, string> = {
  math: "Count the stars with Luna!",
  spelling: "Grow words with Petal!",
  patterns: "Find the patterns with Prism!",
  typing: "Cast letter spells with Whiskers!",
  programming: "Build code adventures with Pixel!",
};

// Background gradients for each discipline card
const DISCIPLINE_GRADIENTS: Record<DisciplineSlug, string> = {
  math: "from-[#1a1a2e] via-[#2d1f4e] to-[#4a2c6e]",
  spelling: "from-[#1a3a2e] via-[#2d4e3f] to-[#3a6e4e]",
  patterns: "from-[#2e1a3a] via-[#4e2d5f] to-[#6e3a7e]",
  typing: "from-[#1a2a3e] via-[#2d3f5e] to-[#3a4e7e]",
  programming: "from-[#3a2a1a] via-[#5e3f2d] to-[#7e4e3a]",
};

interface ChildData {
  id: string;
  displayName: string;
  avatarId: string | null;
  dateOfBirth: string | null;
}

/**
 * PlayPage - Magical game hub showing all disciplines with their character guides.
 *
 * Features:
 * - Beautiful character illustrations instead of emojis
 * - Themed cards for each discipline world
 * - Floating animations and particle effects
 * - Clear progress indicators
 * - Child-friendly large touch targets
 */
export function PlayPage() {
  usePageTitle("Play");

  const selectedChildId = useAppStore((state) => state.selectedChildId);
  const [childName, setChildName] = useState<string | null>(null);

  // Fetch selected child's data to get their name
  useEffect(() => {
    const fetchChildData = async () => {
      if (!selectedChildId) {
        setChildName(null);
        return;
      }

      try {
        const { data } = await apiGet<ChildData>(`/api/children/${selectedChildId}`);
        if (data) {
          setChildName(data.displayName);
        }
      } catch (error) {
        console.error("Failed to load child data:", error);
        setChildName(null);
      }
    };

    fetchChildData();
  }, [selectedChildId]);

  // Get disciplines as array sorted by displayOrder
  const disciplines = Object.values(DISCIPLINES).sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  return (
    <div className="min-h-screen pb-8">
      {/* Magical header */}
      <div className="relative overflow-hidden py-8 px-4">
        {/* Floating sparkles background */}
        <FloatingSparkles />

        <div className="relative z-10 text-center">
          <h1 className="font-display font-extrabold text-display-lg text-neutral-800 mb-2">
            {childName ? `${childName}, Choose Your Adventure` : 'Choose Your Adventure'}
          </h1>
          <p className="font-body text-body-xl text-neutral-600">
            Pick a magical world to explore!
          </p>
        </div>
      </div>

      {/* Discipline cards grid */}
      <div className="px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {disciplines.map((discipline, index) => (
          <DisciplineCard
            key={discipline.slug}
            discipline={discipline.slug as DisciplineSlug}
            name={discipline.name}
            characterName={discipline.characterName}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}

interface DisciplineCardProps {
  discipline: DisciplineSlug;
  name: string;
  characterName: string;
  index: number;
}

function DisciplineCard({
  discipline,
  name,
  characterName,
  index,
}: DisciplineCardProps) {
  const worldName = WORLD_NAMES[discipline];
  const tagline = CHARACTER_TAGLINES[discipline];
  const gradient = DISCIPLINE_GRADIENTS[discipline];
  const glowColor = GLOW_COLORS[discipline];

  return (
    <Link
      to={`/play/${discipline}`}
      className="block focus:outline-none focus-visible:ring-4 focus-visible:ring-sunshine-400 rounded-3xl group"
      style={{
        animationDelay: `${index * 100}ms`,
      }}
    >
      <div
        className={`
          relative
          rounded-3xl
          overflow-hidden
          bg-gradient-to-br ${gradient}
          border-2 border-white/10
          shadow-xl
          transition-all duration-300
          group-hover:scale-[1.02] group-hover:shadow-2xl
          group-hover:border-white/20
          min-h-[280px]
        `}
        style={{
          boxShadow: `0 8px 32px rgba(0,0,0,0.3), 0 0 60px ${glowColor}40`,
        }}
      >
        {/* Twinkling stars background */}
        <TwinklingStars count={20} />

        {/* Character illustration - positioned to the side */}
        <div className="absolute bottom-0 right-0 w-40 h-40 translate-x-4 translate-y-4">
          <CharacterImage
            discipline={discipline}
            expression="happy"
            size="hero"
            floating
            className="opacity-90 group-hover:opacity-100 transition-opacity"
          />
        </div>

        {/* Character glow effect */}
        <div
          className="absolute bottom-0 right-0 w-64 h-64 rounded-full opacity-30 blur-3xl"
          style={{ background: glowColor }}
        />

        {/* Content */}
        <div className="relative z-10 p-6 pr-32">
          {/* World name badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm mb-4 whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-sunshine-400 animate-pulse" />
            <span className="font-display font-semibold text-body-sm text-white/80">
              {worldName}
            </span>
          </div>

          {/* Discipline name */}
          <h2 className="font-display font-extrabold text-display-md text-white mb-2 drop-shadow-lg">
            {name}
          </h2>

          {/* Character name */}
          <p className="font-body text-body-lg text-white/80 mb-3">
            with {characterName}
          </p>

          {/* Tagline */}
          <p className="font-body text-body-md text-white/60 mb-4">
            {tagline}
          </p>

          {/* Progress bar placeholder */}
          <div className="w-full max-w-[200px]">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: "20%",
                  background: `linear-gradient(90deg, ${glowColor}, white)`,
                }}
              />
            </div>
            <p className="mt-2 font-body text-body-sm text-white/50">
              Level 1 of 5
            </p>
          </div>

          {/* Play button indicator */}
          <div className="mt-4 inline-flex items-center gap-2 text-white/70 group-hover:text-white transition-colors">
            <span className="font-display font-bold text-body-lg">
              Play Now
            </span>
            <ChevronRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * Floating sparkles for the header
 */
function FloatingSparkles() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: 30 }).map((_, i) => {
        const colors = [
          "#E8A0FF", // Luna lavender
          "#90EE90", // Petal green
          "#74B9FF", // Prism blue
          "#4A90D9", // Whiskers blue
          "#FF8C42", // Pixel orange
          "#FFD93D", // Sunshine gold
        ];

        return (
          <div
            key={i}
            className="absolute rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${4 + Math.random() * 8}px`,
              height: `${4 + Math.random() * 8}px`,
              background: colors[Math.floor(Math.random() * colors.length)],
              opacity: 0.3 + Math.random() * 0.5,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${3 + Math.random() * 3}s`,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Twinkling stars for discipline cards
 */
function TwinklingStars({ count = 15 }: { count?: number }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${1 + Math.random() * 2}px`,
            height: `${1 + Math.random() * 2}px`,
            opacity: 0.2 + Math.random() * 0.5,
            animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export default PlayPage;
